import { api } from '../api';
import type { EntityTypeRow } from '@/types/domain';

export interface FieldSchemaPreviewResult {
  changes: string[];
  nextVersion: number;
}

export interface EntityTypeUsage {
  entityCount: number;
}

export const entityTypesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listEntityTypes: builder.query<EntityTypeRow[], void>({
      query: () => '/entity-types',
      providesTags: (result) => [
        ...(result ?? []).map(({ id }) => ({ type: 'EntityType' as const, id })),
        { type: 'EntityType', id: 'LIST' },
      ],
    }),
    getEntityType: builder.query<EntityTypeRow, number>({
      query: (id) => `/entity-types/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'EntityType', id }],
    }),
    getEntityTypeUsage: builder.query<EntityTypeUsage, number>({
      query: (id) => `/entity-types/${id}/usage`,
      providesTags: (_r, _e, id) => [{ type: 'EntityType', id: `usage-${id}` }],
    }),
    createEntityType: builder.mutation<EntityTypeRow, Record<string, unknown>>({
      query: (body) => ({ url: '/entity-types', method: 'POST', body }),
      invalidatesTags: [{ type: 'EntityType', id: 'LIST' }],
    }),
    updateEntityType: builder.mutation<EntityTypeRow, { id: number; name?: string; active?: boolean; icon?: string | null }>({
      query: ({ id, ...body }) => ({ url: `/entity-types/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'EntityType', id }, { type: 'EntityType', id: 'LIST' }],
    }),
    updateEntityTypeFieldSchema: builder.mutation<
      EntityTypeRow | FieldSchemaPreviewResult,
      { id: number; fieldSchema: unknown; preview?: boolean; mode?: 'block' | 'allow' }
    >({
      query: ({ id, ...body }) => ({ url: `/entity-types/${id}/field-schema`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id, preview }) => (
        preview ? [] : [{ type: 'EntityType', id }, { type: 'Entity', id: 'LIST' }]
      ),
    }),
    deleteEntityType: builder.mutation<void, number>({
      query: (id) => ({ url: `/entity-types/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'EntityType', id: 'LIST' }],
    }),
  }),
});

export const {
  useListEntityTypesQuery,
  useGetEntityTypeQuery,
  useGetEntityTypeUsageQuery,
  useCreateEntityTypeMutation,
  useUpdateEntityTypeMutation,
  useUpdateEntityTypeFieldSchemaMutation,
  useDeleteEntityTypeMutation,
} = entityTypesApi;
